import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { cn } from 'cn';
import { Button } from '@/components/ui/button';
import { csrfHeaders, toApiError } from '@/lib/http';

/**
 * Asking about one khutbah — and only this khutbah.
 *
 * The rule is the whole feature: the assistant answers from the khutbah's
 * saved text and nothing else. "This khutbah does not say" is a **correct,
 * complete answer**, so it is rendered as one and not as an error.
 *
 * **It never shows an input it cannot answer.** When no provider is
 * configured — which is every environment today — the panel says so plainly
 * instead of accepting a question and returning silence. A reader who types
 * into a dead box learns the wrong thing about whether this product works.
 */

interface Availability {
  readonly available: boolean;
  readonly reason: string | null;
  readonly groundedPassages: number;
}

interface Answer {
  readonly text: string | null;
  readonly passageOrdinals: readonly number[];
  readonly refused: boolean;
  readonly unavailable: boolean;
  readonly unavailableReason: string | null;
}

const STARTERS = [
  'What is this khutbah about?',
  'What does it say about taqwa?',
  'Which verses does it quote?',
];

export function ChatPanel({
  khutbahId,
  language,
  onCitation,
}: {
  khutbahId: string;
  language: string;
  /** Jump the reader to a cited passage. */
  onCitation?: ((passageOrdinal: number) => void) | undefined;
}) {
  const [question, setQuestion] = useState('');
  const [asked, setAsked] = useState<string | null>(null);

  const availability = useQuery({
    queryKey: ['chat', khutbahId, language, 'availability'],
    queryFn: async ({ signal }): Promise<Availability> => {
      const response = await fetch(
        `/api/public/khutbah/${encodeURIComponent(khutbahId)}/chat/availability?language=${encodeURIComponent(language)}`,
        { signal, headers: { Accept: 'application/json' } },
      );
      if (!response.ok) throw await toApiError(response);
      const body = (await response.json()) as Record<string, unknown>;
      return {
        available: body.available === true,
        reason: typeof body.reason === 'string' ? body.reason : null,
        groundedPassages:
          typeof body.groundedPassages === 'number' ? body.groundedPassages : 0,
      };
    },
    staleTime: 5 * 60 * 1000,
  });

  const ask = useMutation({
    retry: false,
    mutationFn: async (text: string): Promise<Answer> => {
      const response = await fetch(
        `/api/public/khutbah/${encodeURIComponent(khutbahId)}/chat?language=${encodeURIComponent(language)}`,
        {
          method: 'POST',
          // CSRF stays on for every write, and a question is a POST. The
          // CsrfFilter runs BEFORE authentication, so a missing token is a
          // 401 rather than a 403 — which looks like "not signed in" and
          // sends you hunting in the wrong place.
          headers: {
            ...(await csrfHeaders(new AbortController().signal)),
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ text }),
        },
      );
      if (!response.ok) throw await toApiError(response);
      const body = (await response.json()) as Record<string, unknown>;
      return {
        text: typeof body.text === 'string' ? body.text : null,
        passageOrdinals: Array.isArray(body.passageOrdinals)
          ? body.passageOrdinals.filter((n): n is number => typeof n === 'number')
          : [],
        refused: body.refused === true,
        unavailable: body.unavailable === true,
        unavailableReason:
          typeof body.unavailableReason === 'string' ? body.unavailableReason : null,
      };
    },
  });

  const unavailable = availability.data && !availability.data.available;

  return (
    // A plain element, not a landmark. The reader's shell already wraps this
    // in the `aside` that carries the label, the border and the heading; this
    // used to be a second `aside` inside it with its own "Ask" heading and its
    // own `border-s`, which drew the rule twice, said the word twice, and
    // nested one complementary landmark inside another.
    <div className="flex h-full flex-col gap-3 p-4">
      {/* The scope, always named. A reader must never be unsure which
          khutbah they are asking about, or think this is general search. */}
      <p className="text-xs text-muted-foreground">
        Answers come only from this khutbah&rsquo;s text. Not a search, and
        not a source of rulings.
      </p>

      {availability.isPending ? (
        <p className="text-sm text-muted-foreground">Checking…</p>
      ) : unavailable ? (
        <UnavailableNotice reason={availability.data.reason} />
      ) : (
        <>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              const text = question.trim();
              if (text === '') return;
              setAsked(text);
              ask.mutate(text);
            }}
            className="flex flex-col gap-2"
          >
            <label className="sr-only" htmlFor="chat-question">
              Your question
            </label>
            <textarea
              id="chat-question"
              value={question}
              onChange={(event) => setQuestion(event.target.value)}
              rows={3}
              maxLength={2000}
              placeholder="What does this khutbah say about…"
              className="w-full resize-y rounded-md border border-border bg-background p-2.5 text-sm outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50"
            />
            <Button
              type="submit"
              size="lg"
              disabled={question.trim() === '' || ask.isPending}
            >
              {ask.isPending ? 'Reading the khutbah…' : 'Ask'}
            </Button>
          </form>

          {!asked ? (
            <div className="flex flex-col gap-1.5">
              <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
                Try
              </p>
              {STARTERS.map((starter) => (
                <button
                  key={starter}
                  type="button"
                  onClick={() => setQuestion(starter)}
                  className="rounded-md border border-border px-2.5 py-1.5 text-start text-[13px] hover:bg-muted"
                >
                  {starter}
                </button>
              ))}
            </div>
          ) : null}
        </>
      )}

      {asked && ask.data ? (
        <AnswerBlock
          question={asked}
          answer={ask.data}
          onCitation={onCitation}
        />
      ) : null}

      {ask.error ? (
        <p role="alert" className="text-sm text-destructive">
          {ask.error instanceof Error ? ask.error.message : 'Something went wrong.'}
        </p>
      ) : null}
    </div>
  );
}

/**
 * Why the assistant cannot answer — shown instead of an input, never
 * alongside one.
 */
function UnavailableNotice({ reason }: { reason: string | null }) {
  return (
    <div className="rounded-lg border border-dashed border-border p-4">
      <p className="text-sm font-semibold">The assistant is not answering yet</p>
      <p className="mt-1 text-[13px] text-pretty text-muted-foreground">
        {reason ?? 'It is not configured.'}
      </p>
      <p className="mt-2 text-xs text-pretty text-muted-foreground">
        There is no question box here on purpose. One that accepted a question
        and returned nothing would be worse than none.
      </p>
    </div>
  );
}

function AnswerBlock({
  question,
  answer,
  onCitation,
}: {
  question: string;
  answer: Answer;
  onCitation?: ((passageOrdinal: number) => void) | undefined;
}) {
  return (
    <div className="flex flex-col gap-2 border-t border-border pt-3">
      <p className="rounded-md bg-chat-user-bg p-2.5 text-[13px] text-chat-user-foreground">
        {question}
      </p>

      {answer.unavailable ? (
        <UnavailableNotice reason={answer.unavailableReason} />
      ) : (
        <div
          className={cn(
            'rounded-md p-2.5 text-sm',
            // A refusal is a complete answer, so it gets the same ground as
            // any other — not an error colour.
            'bg-muted',
          )}
        >
          <p className="text-pretty">{answer.text}</p>

          {answer.refused ? (
            <p className="mt-1.5 text-xs text-muted-foreground">
              That is a complete answer, not a failure — the assistant has no
              sources beyond this khutbah.
            </p>
          ) : null}

          {answer.passageOrdinals.length > 0 ? (
            <p className="mt-2 flex flex-wrap items-center gap-1.5">
              <span className="text-[11px] uppercase tracking-[0.06em] text-muted-foreground">
                From
              </span>
              {answer.passageOrdinals.map((ordinal) => (
                <button
                  key={ordinal}
                  type="button"
                  onClick={() => onCitation?.(ordinal)}
                  className="rounded-sm bg-citation-bg px-1.5 py-0.5 text-[12px] font-semibold text-citation-foreground hover:underline"
                >
                  Passage {ordinal}
                </button>
              ))}
            </p>
          ) : null}
        </div>
      )}
    </div>
  );
}
